import JSONModel from "sap/ui/model/json/JSONModel";
import BaseController from "./BaseController";
import SideNavigation from "sap/tnt/SideNavigation";
import Event from "sap/ui/base/Event";
import User from "../model/ts/User";
import LogHandler from "../model/ts/handlers/LogHandler";
import { MenuItem } from "../model/ts/Menu";
import PHPBackendRequest from "../model/ts/handlers/PHPBackendRequest";
import PersonalizationUtils from "../model/ts/subtasks/utils/PersonalizationUtils";
import { Router$RoutePatternMatchedEvent } from "sap/ui/core/routing/Router";
import NavigationList from "sap/tnt/NavigationList";
import NavigationListItem from "sap/tnt/NavigationListItem";
/**
 * @namespace sap.mcsuite.controller
 */
export default class Main extends BaseController {
	onInit(): void {
		//this.disableBusyIndicator();
		try {
			this.loadMenuModel();
		}
		catch (error) {
			console.error("Error loading menu model:", error);
		}

		try {
			this.loadUserModel();
		}
		catch (error) {
			console.error("Error loading user model:", error);
		}
		this.getOwnerComponent().getRouter().getRoute("task").attachPatternMatched(this.onTaskMatched, this);
		this.getRouter().getRoute("index").attachPatternMatched(this.onIndexRoute, this);

	}

	onIndexRoute(): void {
		this.loadMenuModel();		
	}

	/**
	 * Disables the busy indicator for the view.
	 * This method is called during the initialization of the controller.
	 */
	/*private disableBusyIndicator(): void {
		document
			.getElementById("busyIndicator")
			?.setAttribute("style", "display: none;");
	}*/
	/**
	 * Loads the menu model and sets it to the view.
	 * If the menu is not initialized, it attaches an event listener to update the model when the menu changes.
	 */
	/*public loadMenuModel(): void {
		const menu = this.getOwnerComponent().getMenu();
		this.getOwnerComponent().setModel(new JSONModel({}), "menu");
		if (!menu.isInitialized()) {
			menu.attachMenuChange(
				(
					event: Event & {
						mParameters: { menuConfig: { menu: Array<MenuItem> } };
					},
				) => {
					const menuData = event.mParameters.menuConfig;
					const model = this.getOwnerComponent().getModel("menu") as JSONModel;
					model.setData(menuData);
					this.addBadgesToMenuConfig(menuData)
						.then((updatedMenuData) => {
							model.setData(updatedMenuData);
						})
						.catch((error) => {
							console.error("Error updating menu data with badges:", error);
						});
				}
			);
		} else {
			const menuData = menu.getMenuConfig();
			const model = this.getOwnerComponent().getModel("menu") as JSONModel;
			model.setData(menuData);
			this.addBadgesToMenuConfig(menuData)
				.then((updatedMenuData) => {
					model.setData(updatedMenuData);
				})
				.catch((error) => {
					console.error("Error updating menu data with badges:", error);
				});
		}
	}*/

	public loadMenuModel(): void {
		const component = this.getOwnerComponent();
		const menu = component.getMenu();
		const model = new JSONModel({});

		// setData replaces everything, so keep the current title each time
		const setMenu = (menuData: { menu: Array<MenuItem> }) => {
			model.setData({ ...menuData, title: model.getProperty("/title") });
			this.addBadgesToMenuConfig(menuData)
				.then((updated) => {
					model.setData({ ...updated, title: model.getProperty("/title") });
				})
				.catch((error) => {
					console.error("Error updating menu data with badges:", error);
				});
		};
		component.setModel(model, "menu");

		// App title from translations
		sap.ui.require(["sap/mcsuite/model/Translate"], (Translate: any) => {
			Translate.Label.get("LITERAL", "main_title", "main_title").then((text: string) => {
				model.setProperty("/title", text || "");
			});
		});

		if(!menu.isInitialized()) {
			menu.attachMenuChange(
				(event: Event & { mParameters: { menuConfig: { menu: Array<MenuItem> } } }) => {
					setMenu(event.mParameters.menuConfig);
				}
			);
		} else {
			setMenu(menu.getMenuConfig());
		}
	}

	/**
	 * Retrieves user data from the User instance and returns it as a record.
	 * @param user - The User instance from which to retrieve data.
	 * @returns An object containing user data.
	 */
	private getUserData(user: User): Record<string, unknown> {
		return {
			username: user.getUsername(),
			scopes: user.getScopes(),
			email: user.getEmail(),
			firstName: user.getFirstName(),
			lastName: user.getLastName(),
			locale: user.getLocale(),
			id: user.getId()
		};
	}

	/**
	 * Loads the user model and sets it to the view.
	 */
	public loadUserModel(): void {
		const user = this.getOwnerComponent().getUser();
		this.getOwnerComponent().setModel(new JSONModel({}), "user");
		const userModel = this.getOwnerComponent().getModel("user") as JSONModel;
		if (!user.isAuthenticated()) {
			LogHandler.display("User is not authenticated, attaching authenticated event.");
			user.attachAuthenticated(() => {
				userModel.setData(this.getUserData(user));
				PersonalizationUtils.loadPersonalization(this)
					.then(() => {
						LogHandler.display("Personalization loaded successfully.");
					})
					.catch((error) => {
						console.error("Error loading personalization:", error);
					});
			});
		}
		else {
			LogHandler.display("User is already authenticated, loading user data.");
			userModel.setData(this.getUserData(user));
			PersonalizationUtils.loadPersonalization(this)
				.then(() => {
					LogHandler.display("Personalization loaded successfully.");
				})
				.catch((error) => {
					console.error("Error loading personalization:", error);
				});
		}
	}

	/**
	 * Retrieves the modules from the menu configuration.
	 * This method extracts the target modules from the menu items in the provided menu configuration.
	 * @param menuConfig - The menu configuration object containing an array of MenuItem objects.
	 * @returns An array of module names extracted from the menu configuration.
	 */
	private getModulesFromMenuConfig(menuConfig: {
		menu: Array<MenuItem>;
	}): string[] {
		const modules: string[] = [];
		for (const item of menuConfig.menu) {
			if (item.target) {
				modules.push(item.target);
			}
		}
		return modules;
	}


	/**
	 * Adds badges to the menu configuration.
	 * This method iterates through the modules in the menu configuration and fetches badge data for each module.
	 * The fetched badge data is then added to the corresponding menu item in the configuration.
	 * @param menuConfig - The menu configuration object containing an array of MenuItem objects.
	 * @returns A promise that resolves to the updated menu configuration with badges added.
	 */
	private async addBadgesToMenuConfig(menuConfig: {
		menu: Array<MenuItem>;
	}): Promise<{ menu: Array<MenuItem> }> {
		const modules = this.getModulesFromMenuConfig(menuConfig);
		for (const module of modules) {
			try {
				const menuItem = menuConfig.menu.find((item) => item.target === module);
				if (!menuItem) {
					console.warn(
						`Menu item for module ${module} not found in menu configuration.`,
					);
					continue;
				}
				let badge = 0;
				let badgeData = null;
	
				if (Array.isArray(module)) {
					for (const value of module) {
						const res = await this.fetchBadge(value);
						badge = badge + res.count;
						badgeData = res.data; // Overwrite
					}
				} else {
					const res = await this.fetchBadge(module);
					badge = res.count;
					badgeData = res.data;
				}

				let sBadgeClass = 'index-tile-kpi';
				if(badge > 99) sBadgeClass = 'index-tile-kpi-small';
				menuItem.badge = {
					amount: Number(badge),
					class: String(sBadgeClass),
					data: badgeData,
				};
			} catch (error) {
				console.error(`Error loading badge for module ${module}:`, error);
			}
		}
		LogHandler.display('menu config', menuConfig);
		return menuConfig;
	}


	/**
	 * Fetches the badge data for a given module.
	 * This method sends a request to the PHP backend to retrieve the badge count for the specified module.
	 * @param module - The name of the module for which to fetch the badge data.
	 * @returns A promise that resolves to the badge count as a number.
	 * @throws {Error} If the request fails or if the response is not in the expected format.
	 */
	private async fetchBadge(module: string): Promise<{ count: number; data: any }> {
		const phpRequest = new PHPBackendRequest(
			module.toUpperCase(),
			"BADGES",
			this.getOwnerComponent().getUser(),
		);
		try {
			const badgeResponse = await phpRequest.send();
			if (!badgeResponse.ok) {
				console.error(
					`Failed to fetch badge data for module ${module}:`,
					badgeResponse.statusText,
				);
				throw new Error(
					`Could not fetch badge data for module ${module}: ${badgeResponse.statusText}`,
				);
			}
			if (
				!badgeResponse.headers.get("Content-Type")?.includes("application/json")
			) {	
				console.error(`Response for module ${module} is not in JSON format.`);
				throw new Error("Response is not in JSON format");
			}
			const badgeData = (await badgeResponse.json()) as { count: string; data: any };
			const count = parseInt(badgeData.count, 10); // 10 is the radix for decimal numbers
			if (isNaN(count)) {
				console.error(
					`Invalid badge data format for module ${module}:`,
					badgeData,
				);
				throw new Error("Invalid badge data format");
			}
			return {
				count,
				data: badgeData.data,
			};
		} catch (error) {
			if (error instanceof Error) {
				console.error(
					`Failed to fetch badge data for module ${module}:`,
					error,
				);
				throw error;
			} else {
				console.error(
					`An unexpected error occurred while fetching badge data for module ${module}:`,
					error,
				);
				throw new Error(
					"An unexpected error occurred while fetching badge data.",
				);
			}
		}
	}

	// EVENTS
	/**
	 * Toggles the side navigation's expanded state.
	 * This method is called when the user clicks on the side navigation toggle button.
	 * It retrieves the side navigation control by its ID and toggles its expanded state.
	 * @throws {Error} If the side navigation control is not found or is not an instance of SideNavigation.
	 * */
	public onToggleSideNavigation(): void {
		const sideNavigation = this.byId("SideNavigation");
		if (sideNavigation instanceof SideNavigation) {
			sideNavigation.setExpanded(!sideNavigation.getExpanded());
		} else {
			throw new Error(
				"SideNavigation not found or is not an instance of SideNavigation.",
			);
		}
	}

	public navToHelpPage(): void {
		this.navTo('help');
	}

	private onTaskMatched(event: Router$RoutePatternMatchedEvent): void {
		// Deselect menu item when navigating to task route
		const sideNavigation = this.byId("navigationList") as NavigationList;
		const items = sideNavigation.getItems();
		const args = event.getParameter("arguments") as { module?: string, task?: string };
		if (args && args.module) {
			const selectedItem = items.find(item => item.getKey() === args.module) as NavigationListItem;
			if (selectedItem) {
				sideNavigation.setSelectedItem(selectedItem);
			}
		}
	}
}
